import { MainSection } from "@/styles/global";
import React from "react";
import S from "./FormGiftfunds.styled";
import UploadIcon from "@/assets/upload.svg";
import axios from "axios";
import FormSuccess from "@/components/global-components/FormSuccess";
import { useForm } from "react-hook-form";

export interface FormGiftfundsProps {}

type FormData = {
  dovas_request_category: string;
  ansokanFor?: string;
  sokandeAr?: string;
  namn?: string;
  personNummer?: string;
  adress: string;
  postNummer: string;
  stad: string;
  email: string;
  soktBelopp: string;
  inkomst?: string;
  makeMakaInkomst?: string;
  syfte: string;
  andraBidrag: string;
  bidragText?: string;
  file?: File;
  taxFile?: File;
  ovrigt?: string;
  antalDeltagare?: string;
  organisationsNamn?: string;
  organisationsNummer?: string;
  reference?: string;
};

type FormState = {
  isLoading: boolean;
  error?: string;
  data: FormData;
  success: boolean;
};

const emptyForm = (): FormData => ({
  dovas_request_category: "Gåvomedel",
  ansokanFor: "Enskild person",
  adress: "",
  email: "",
  syfte: "",
  postNummer: "",
  stad: "",
  soktBelopp: "",
  andraBidrag: "",
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  data: emptyForm(),
  success: false,
});

const FormGiftfunds: React.FC<FormGiftfundsProps> = () => {
  const [active, setActive] = React.useState("Enskild person");
  const [formState, setFormState] = React.useState(emptyFormState());

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // update form data
  const updateFormProperty = <K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ): void => {
    setFormState((state: FormState) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  };

  const submitForm = handleSubmit(() => {
    setFormState((state) => ({
      ...state,
      isLoading: true,
    }));

    const formData = new FormData();

    // Lägg till varje fält i formData
    (Object.keys(formState.data) as (keyof FormData)[]).forEach((key) => {
      if (key === "file" && formState.data.file) {
        formData.append(key, formState.data.file);
      } else if (key === "taxFile" && formState.data.taxFile) {
        formData.append(key, formState.data.taxFile);
      } else {
        formData.append(key, formState.data[key] as string);
      }
    });

    axios
      .post(process.env.GATSBY_CONTACT_URL || "", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (!res.data.success) {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            error: res.data.error_msg,
          }));
        } else {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            success: true,
          }));
        }
      })
      .catch((error) => {
        setFormState((state) => ({
          ...state,
          isLoading: false,
          error: "Något gick fel, vänligen försök igen.",
        }));
      });
  });

  const removeFile = (type: string) => {
    // remove file from form data
    return () => {
      if (type === "file") {
        setFormState((state) => ({
          ...state,
          data: {
            ...state.data,
            file: undefined,
          },
        }));
      } else {
        setFormState((state) => ({
          ...state,
          data: {
            ...state.data,
            taxFile: undefined,
          },
        }));
      }
    };
  };

  return (
    <MainSection>
      <S.Inner>
        {formState.success ? (
          <FormSuccess />
        ) : (
          <S.FormWrapper>
            <S.H4>
              Du ansöker till Stockholm Dövas Förening, stiftelse för gåvomedel
            </S.H4>
            <S.Wrapper>
              <S.H6>Det gäller</S.H6>
              <S.FlexWrapper>
                <S.Button
                  className={active === "Enskild person" ? "active" : ""}
                  onClick={() => {
                    setActive("Enskild person");
                    updateFormProperty("ansokanFor", "Enskild person");
                  }}
                >
                  Enskild person
                </S.Button>
                <S.Button
                  className={active === "Organisation" ? "active" : ""}
                  onClick={() => {
                    setActive("Organisation");
                    updateFormProperty("ansokanFor", "Organisation");
                  }}
                >
                  Organisation/ grupp/ förening
                </S.Button>
              </S.FlexWrapper>
            </S.Wrapper>
            <S.Wrapper>
              <S.DetailsLarge>Obligatoriska fält</S.DetailsLarge>
            </S.Wrapper>
            <S.Form onSubmit={submitForm}>
              {active === "Enskild person" ? (
                <>
                  <S.Wrapper>
                    <S.Label>Sökande är</S.Label>
                    <S.FlexWrapper>
                      <div>
                        <S.RadioButton
                          type="radio"
                          id="sokandeAr"
                          {...register("sokandeAr", { required: true })}
                          className={
                            errors.sokandeAr
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          onChange={() =>
                            updateFormProperty("sokandeAr", "Döv/hörselskadad")
                          }
                        />
                        <S.SmallLabel htmlFor="sokandeAr">
                          Döv/hörselskadad
                        </S.SmallLabel>
                      </div>
                      <div>
                        <S.RadioButton
                          type="radio"
                          id="sokandeAr"
                          {...register("sokandeAr", { required: true })}
                          className={
                            errors.sokandeAr
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          onChange={() =>
                            updateFormProperty("sokandeAr", "Hörande")
                          }
                        />
                        <S.SmallLabel htmlFor="sokandeAr">Hörande</S.SmallLabel>
                      </div>
                    </S.FlexWrapper>
                    {errors.sokandeAr && (
                      <S.Error role="alert">Välj ett alternativ</S.Error>
                    )}
                  </S.Wrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="name">Ditt namn</S.Label>
                    <S.Input
                      type="text"
                      {...register("namn", { required: true })}
                      className={errors.namn ? "error" : ""}
                      placeholder="Ditt namn"
                      value={formState.data.namn}
                      onChange={(e) =>
                        updateFormProperty("namn", e.target.value)
                      }
                    />
                    {errors.namn && (
                      <S.Error role="alert">Namn är obligatoriskt</S.Error>
                    )}
                  </S.Wrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="personNummer">Personnummer</S.Label>
                    <S.Input
                      type="text"
                      {...register("personNummer", { required: true })}
                      className={errors.personNummer ? "error" : ""}
                      placeholder="Personnummer"
                      onChange={(e) =>
                        updateFormProperty("personNummer", e.target.value)
                      }
                    />
                    {errors.personNummer && (
                      <S.Error role="alert">
                        Personnummer är obligatoriskt
                      </S.Error>
                    )}
                  </S.Wrapper>
                </>
              ) : (
                <>
                  <S.Wrapper>
                    <S.Label htmlFor="antalDeltagare">Antal deltagare</S.Label>
                    <S.Input
                      type="string"
                      {...register("antalDeltagare", { required: true })}
                      className={errors.antalDeltagare ? "error" : ""}
                      placeholder="Antal deltagare"
                      onChange={(e) =>
                        updateFormProperty("antalDeltagare", e.target.value)
                      }
                    />
                    {errors.antalDeltagare && (
                      <S.Error role="alert">
                        Antal deltagare är obligatoriskt
                      </S.Error>
                    )}
                  </S.Wrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="organisationsNamn">
                      Organisationsnamn
                    </S.Label>
                    <S.Input
                      type="text"
                      {...register("organisationsNamn", { required: true })}
                      className={errors.organisationsNamn ? "error" : ""}
                      placeholder="Organisationsnamn"
                      onChange={(e) =>
                        updateFormProperty("organisationsNamn", e.target.value)
                      }
                    />
                    {errors.organisationsNamn && (
                      <S.Error role="alert">
                        Organisationsnamn är obligatoriskt
                      </S.Error>
                    )}
                  </S.Wrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="organisationsNummer">
                      Organisationsnummer
                    </S.Label>
                    <S.Input
                      type="text"
                      {...register("organisationsNummer", { required: true })}
                      className={errors.organisationsNummer ? "error" : ""}
                      placeholder="Organisationsnummer"
                      onChange={(e) =>
                        updateFormProperty(
                          "organisationsNummer",
                          e.target.value
                        )
                      }
                    />
                    {errors.organisationsNummer && (
                      <S.Error role="alert">
                        Organisationsnummer är obligatoriskt
                      </S.Error>
                    )}
                  </S.Wrapper>
                </>
              )}
              <S.Wrapper>
                <S.Label htmlFor="adress">Adress</S.Label>
                <S.Input
                  type="text"
                  {...register("adress", { required: true })}
                  className={errors.adress ? "error" : ""}
                  placeholder="Adress"
                  onChange={(e) => updateFormProperty("adress", e.target.value)}
                />
                {errors.adress && (
                  <S.Error role="alert">Adress är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.FlexWrapper>
                <S.Wrapper>
                  <S.Label htmlFor="postNummer">Postnummer</S.Label>
                  <S.Input
                    type="text"
                    {...register("postNummer", { required: true })}
                    className={errors.postNummer ? "error" : ""}
                    placeholder="Postnummer"
                    onChange={(e) =>
                      updateFormProperty("postNummer", e.target.value)
                    }
                  />
                  {errors.postNummer && (
                    <S.Error role="alert">Postnummer är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
                <S.Wrapper>
                  <S.Label htmlFor="stad">Postort</S.Label>
                  <S.Input
                    type="text"
                    {...register("stad", { required: true })}
                    className={errors.stad ? "error" : ""}
                    placeholder="Postort"
                    onChange={(e) => updateFormProperty("stad", e.target.value)}
                  />
                  {errors.stad && (
                    <S.Error role="alert">Postort är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
              </S.FlexWrapper>
              {active === "Enskild person" ? (
                <S.Wrapper>
                  <S.Label htmlFor="email">E-post</S.Label>
                  <S.Input
                    type="email"
                    {...register("email", { required: true })}
                    className={errors.email ? "error" : ""}
                    placeholder="E-post"
                    onChange={(e) =>
                      updateFormProperty("email", e.target.value)
                    }
                  />
                  {errors.email && (
                    <S.Error role="alert">E-post är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
              ) : (
                <S.Wrapper>
                  <S.Label htmlFor="email">Kontaktpersons e-post</S.Label>
                  <S.Input
                    type="email"
                    {...register("email", { required: true })}
                    className={errors.email ? "error" : ""}
                    placeholder="E-post"
                    onChange={(e) =>
                      updateFormProperty("email", e.target.value)
                    }
                  />
                  {errors.email && (
                    <S.Error role="alert">E-post är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
              )}
              <S.Wrapper>
                <S.Label htmlFor="soktBelopp">Sökt belopp</S.Label>
                <S.Input
                  {...register("soktBelopp", { required: true })}
                  className={errors.soktBelopp ? "error" : ""}
                  type="number"
                  placeholder="Sökt belopp"
                  onChange={(e) =>
                    updateFormProperty("soktBelopp", e.target.value)
                  }
                />
                {errors.soktBelopp && (
                  <S.Error role="alert">Sökt belopp är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              {active === "Enskild person" && (
                <S.FlexWrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="inkomst">Sökandes årsinkomst</S.Label>
                    <S.Input
                      type="text"
                      {...register("inkomst", { required: true })}
                      className={errors.inkomst ? "error" : ""}
                      placeholder="Sökandes årsinkomst"
                      onChange={(e) =>
                        updateFormProperty("inkomst", e.target.value)
                      }
                    />
                    {errors.inkomst && (
                      <S.Error role="alert">
                        Sökandes årsinkomst är obligatoriskt
                      </S.Error>
                    )}
                  </S.Wrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="makeMakaInkomst">
                      Maka/makes årsinkomst
                    </S.Label>
                    <S.Input
                      type="text"
                      {...register("makeMakaInkomst", { required: true })}
                      className={errors.makeMakaInkomst ? "error" : ""}
                      placeholder="Maka/makes årsinkomst"
                      onChange={(e) =>
                        updateFormProperty("makeMakaInkomst", e.target.value)
                      }
                    />
                    {errors.makeMakaInkomst && (
                      <S.Error role="alert">
                        Maka/makes årsinkomst är obligatoriskt
                      </S.Error>
                    )}
                  </S.Wrapper>
                </S.FlexWrapper>
              )}
              <S.Wrapper>
                <S.Label htmlFor="syfte">Beskriv ändamålet</S.Label>
                <S.TextArea
                  {...register("syfte", { required: true })}
                  className={errors.syfte ? "error" : ""}
                  placeholder="Ändamål"
                  onChange={(e) => updateFormProperty("syfte", e.target.value)}
                />
                {errors.syfte && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label>Har du sökt andra bidrag?</S.Label>
                <S.FlexWrapper>
                  <div>
                    <S.RadioButton
                      type="radio"
                      {...register("andraBidrag", { required: true })}
                      className={
                        errors.andraBidrag
                          ? "error custom-radio"
                          : "custom-radio"
                      }
                      onChange={() => updateFormProperty("andraBidrag", "yes")}
                    />
                    <S.SmallLabel htmlFor="andraBidrag">Ja</S.SmallLabel>
                  </div>
                  <div>
                    <S.RadioButton
                      type="radio"
                      {...register("andraBidrag", { required: true })}
                      className={
                        errors.andraBidrag
                          ? "error custom-radio"
                          : "custom-radio"
                      }
                      onChange={() => updateFormProperty("andraBidrag", "no")}
                    />
                    <S.SmallLabel htmlFor="andraBidrag">Nej</S.SmallLabel>
                  </div>
                </S.FlexWrapper>
                {errors.andraBidrag && (
                  <S.Error role="alert">Välj ett alternativ</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="bidragText" className="not-required">
                  Om ja, vilka bidrag har du sökt?
                </S.Label>
                <S.Input
                  type="text"
                  name="bidragText"
                  placeholder="Vilka bidrag"
                  onChange={(e) =>
                    updateFormProperty("bidragText", e.target.value)
                  }
                />
              </S.Wrapper>
              {active === "Enskild person" && (
                <S.Wrapper>
                  <S.Label className="not-required" htmlFor="tax-file">
                    Kopia av senaste slutskattebesked
                  </S.Label>
                  <S.SmallLabel>Endast PDF</S.SmallLabel>

                  <S.Attachments htmlFor="tax-file">
                    Ladda upp från dator {<UploadIcon />}
                  </S.Attachments>
                  <S.UploadButton
                    name="tax-file"
                    id="tax-file"
                    type="file"
                    accept=".pdf"
                    onChange={(event) =>
                      updateFormProperty(
                        "taxFile",
                        (event.target.files || [])[0]
                      )
                    }
                  />
                  {formState.data.taxFile && (
                    <S.FileWrapper>
                      <S.FileText>{formState.data.taxFile.name}</S.FileText>
                      <svg
                        onClick={removeFile("taxFile")}
                        width="15"
                        height="15"
                        viewBox="0 0 12 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.5 1.10786L10.3921 0L6 4.39214L1.60786 0L0.5 1.10786L4.89214 5.5L0.5 9.89214L1.60786 11L6 6.60786L10.3921 11L11.5 9.89214L7.10786 5.5L11.5 1.10786Z"
                          fill="#171131"
                        />
                      </svg>
                    </S.FileWrapper>
                  )}
                </S.Wrapper>
              )}
              <S.Wrapper>
                <S.Label className="not-required">Bifoga filer</S.Label>
                <S.SmallLabel>Endast PDF</S.SmallLabel>

                <S.Attachments htmlFor="file" className="custom-file-upload">
                  Ladda upp från dator {<UploadIcon />}
                </S.Attachments>
                <S.UploadButton
                  name="file"
                  id="file"
                  type="file"
                  accept=".pdf"
                  onChange={(event) =>
                    updateFormProperty("file", (event.target.files || [])[0])
                  }
                />
                {formState.data.file && (
                  <S.FileWrapper>
                    <S.FileText>{formState.data.file.name}</S.FileText>
                    <svg
                      onClick={removeFile("file")}
                      width="15"
                      height="15"
                      viewBox="0 0 12 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.5 1.10786L10.3921 0L6 4.39214L1.60786 0L0.5 1.10786L4.89214 5.5L0.5 9.89214L1.60786 11L6 6.60786L10.3921 11L11.5 9.89214L7.10786 5.5L11.5 1.10786Z"
                        fill="#171131"
                      />
                    </svg>
                  </S.FileWrapper>
                )}
              </S.Wrapper>
              {active === "Organisation" && (
                <S.Wrapper>
                  <S.Label htmlFor="reference" className="not-required">
                    Referenser
                  </S.Label>
                  <S.SmallLabel>Namn och kontaktuppgifter</S.SmallLabel>
                  <S.Input
                    type="text"
                    name="reference"
                    placeholder="Referenser"
                  />
                </S.Wrapper>
              )}

              <S.Wrapper>
                <S.Label htmlFor="ovrigt" className="not-required">
                  Övriga upplysningar
                </S.Label>
                <S.SmallLabel>Här kan du ange övriga upplysningar</S.SmallLabel>
                <S.TextArea
                  name="ovrigt"
                  placeholder="Övriga upplysningar"
                  onChange={(e) => updateFormProperty("ovrigt", e.target.value)}
                />
              </S.Wrapper>
              <S.Wrapper>
                <S.CheckboxContainer>
                  <S.CheckboxWrapper>
                    <S.Checkbox
                      type="checkbox"
                      {...register("accept", { required: true })}
                      className={errors.accept ? "error" : ""}
                    />
                    <svg
                      width="15"
                      height="13"
                      viewBox="0 0 15 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.1667 2.52002L5.37504 10.3117L1.83337 6.77002"
                        stroke="#281E78"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </S.CheckboxWrapper>
                  <S.SmallLabel htmlFor="accept">
                    Jag godkänner Stockholm Dövas Förenings{" "}
                    <a href="/">integritetspolicy.</a>
                  </S.SmallLabel>
                </S.CheckboxContainer>
              </S.Wrapper>
              <S.Wrapper>
                <S.SubmitButton type="submit">Skicka</S.SubmitButton>
                {formState.error && (
                  <S.FormError>
                    Något gick fel, vänligen försök igen.
                  </S.FormError>
                )}
              </S.Wrapper>
            </S.Form>
          </S.FormWrapper>
        )}
      </S.Inner>
    </MainSection>
  );
};

export default FormGiftfunds;
